import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, num, ClauseBlock, FieldRow, KeepTogether, Section, SectionNumberingProvider, SigningBlock, TemplateDocument, TemplateFooter, TemplateLetterhead, AttachmentList, attachmentRefLabel, } from '../../../atoms/index.js';
import { expandPartyProjection, PartyBlock } from '../../../parties/index.js';
import { parseDateValue, parseNumberValue, postSigningRule, subtractMonths, } from '../../../notification-rules.js';
/**
 * Notification rules (R5). Bilplatsavtal är alltid tidsbegränsat
 * (jfr Lokalhyra). Två lifecycle-händelser:
 *   - termination_notice — end_date − notice_months
 *   - lease_end          — end_date
 */
const bilplatsNotificationRules = [
    postSigningRule({
        ruleKey: 'termination_notice',
        kind: 'termination_notice',
        label: 'Uppsägning',
        resolveTriggerAt: (values) => {
            const endDate = parseDateValue(values.end_date);
            const notice = parseNumberValue(values.notice_months);
            if (!endDate || notice === null || notice <= 0)
                return null;
            return subtractMonths(endDate, notice);
        },
    }),
    postSigningRule({
        ruleKey: 'lease_end',
        kind: 'lease_end',
        label: 'Bilplatsavtalet löper ut',
        occurrenceLabel: 'Bilplatsavtalet löper ut',
        resolveTriggerAt: (values) => parseDateValue(values.end_date),
    }),
];
export const metadata = {
    // Document sections in fixed order (per ADR-0049). § numbers are
    // computed from visibility; refs are the stable identity. Field-less
    // sections (static text / signing / bilagor) are declared so they
    // get numbered even though they carry no editor field.
    sections: [
        { ref: 'upplatare', title: 'Upplåtare' },
        { ref: 'nyttjanderattshavare', title: 'Nyttjanderättshavare' },
        { ref: 'bilplats-lage', title: 'Bilplatsens läge, nummer och typ' },
        { ref: 'anvandning', title: 'Användning' },
        { ref: 'samnyttjande', title: 'Samnyttjande' },
        { ref: 'elstrom', title: 'Elström' },
        { ref: 'upplatelsetid', title: 'Upplåtelsetid' },
        { ref: 'uppsagning', title: 'Uppsägningstid / Förlängningstid' },
        { ref: 'avgift', title: 'Avgift' },
        { ref: 'moms', title: 'Mervärdesskatt (moms)' },
        { ref: 'andring-avgift', title: 'Ändring av avgift' },
        { ref: 'betalning', title: 'Avgiftens betalning' },
        { ref: 'avtalsvillkor', title: 'Avtalsvillkor' },
        { ref: 'bilagor', title: 'Bilagor' },
        { ref: 'underskrift', title: 'Underskrifter' },
    ],
    inputSections: [
        { id: 'objektet', label: 'Bilplats & villkor', sectionRefs: ['bilplats-lage', 'anvandning', 'samnyttjande', 'elstrom'] },
        { id: 'upplatelse', label: 'Upplåtelsetid', sectionRefs: ['upplatelsetid', 'uppsagning'] },
        { id: 'avgift', label: 'Avgift', sectionRefs: ['avgift', 'moms', 'andring-avgift', 'betalning'] },
    ],
    templateKey: 'bilplats',
    version: 'v1',
    category: 'Bilplats',
    title: 'Bilplatsavtal · Version 2026',
    shortTitle: 'Bilplatsavtal',
    titleSuffix: { key: 'bilplats_number', prefix: 'plats' },
    formNumber: 'Formulär R-05',
    preamble: 'Mellan upplåtare och nyttjanderättshavare har följande avtal om upplåtelse av mark för bilplats ingåtts.',
    status: 'active',
    signingPlacementAnchors: 'per-party-label',
    partySlots: [
        {
            id: 'lessor',
            label: 'Upplåtare',
            role: 'lessor',
            ordinal: 0,
            required: true,
        },
        {
            id: 'user',
            label: 'Nyttjanderättshavare',
            role: 'lessee',
            ordinal: 0,
            required: true,
        },
    ],
    // Party projection (ADR-0065).
    partyProjection: [
        {
            slot: 'lessor',
            section: 'upplatare',
            fields: [
                { attr: 'legal_name', key: 'lessor_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'lessor_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'lessor_address', requiredAtFinal: true },
                { attr: 'phone', key: 'lessor_phone' },
            ],
        },
        {
            slot: 'user',
            section: 'nyttjanderattshavare',
            fields: [
                { attr: 'legal_name', key: 'user_name', requiredAtFinal: true },
                { attr: 'id_number', key: 'user_org_number', requiredAtFinal: true },
                { attr: 'address', key: 'user_address', requiredAtFinal: true },
                { attr: 'phone', key: 'user_phone' },
            ],
        },
    ],
    notificationRules: bilplatsNotificationRules,
};
export const variableSchema = [
    // §1 Upplåtare / §2 Nyttjanderättshavare — generated from
    // metadata.partyProjection (ADR-0065).
    ...expandPartyProjection(metadata),
    // §3 Bilplatsens läge, nummer och typ
    {
        key: 'property_municipality',
        label: 'Kommun',
        type: 'text',
        section: 'bilplats-lage',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'kommun' },
    },
    {
        key: 'property_designation',
        label: 'Fastighetsbeteckning',
        type: 'text',
        section: 'bilplats-lage',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'name' },
    },
    {
        key: 'bilplats_address',
        label: 'Bilplatsens adress',
        type: 'textarea',
        section: 'bilplats-lage',
        requiredAtFinal: true,
        autofill: { kind: 'property', field: 'address' },
    },
    {
        key: 'bilplats_number',
        label: 'Bilplatsnummer',
        type: 'text',
        section: 'bilplats-lage',
        visibleWhen: { bilplats_no_specific_spot: false },
        // Per-unit identifier — never carried over by copy-from (ADR-0050).
        scopes: { copy: 'never' },
    },
    {
        key: 'bilplats_no_specific_spot',
        label: 'Ingen särskild angiven plats',
        type: 'boolean',
        section: 'bilplats-lage',
        default: false,
    },
    {
        key: 'roof_status',
        label: 'Tak',
        type: 'select',
        section: 'bilplats-lage',
        requiredAtFinal: true,
        options: [
            { value: 'Har tak', label: 'Har tak', code: 'har_tak', summary: 'The parking space is covered (has a roof).' },
            { value: 'Har inte tak', label: 'Har inte tak', code: 'inget_tak', summary: 'The parking space is uncovered (no roof).' },
        ],
    },
    {
        key: 'area_sqm',
        label: 'Yta (m²)',
        type: 'number',
        min: 0,
        section: 'bilplats-lage',
    },
    // §4 Användning
    {
        key: 'car_count',
        label: 'Antal personbilar',
        type: 'number',
        min: 0,
        integer: true,
        section: 'anvandning',
        requiredAtFinal: true,
        default: 1,
    },
    {
        key: 'ev_count',
        label: 'Antal eldrivna personbilar med laddning',
        type: 'number',
        min: 0,
        integer: true,
        section: 'anvandning',
        default: 0,
    },
    // §5 Samnyttjande
    {
        key: 'shared_use',
        label: 'Bilplatsen får nyttjas',
        type: 'select',
        section: 'samnyttjande',
        requiredAtFinal: true,
        default: 'Alla dagar och tider',
        options: [
            { value: 'Alla dagar och tider', label: 'Alla dagar och tider', code: 'alla_tider', summary: 'The space may be used all days and hours.' },
            { value: 'Endast nedanstående dagar och tider', label: 'Endast nedanstående dagar och tider', code: 'angivna_tider', summary: 'The space may be used only on the days and hours specified below.' },
            { value: 'Enligt bilaga', label: 'Enligt bilaga', code: 'enligt_bilaga', summary: 'Usage times are set out in an attached annex.' },
        ],
    },
    {
        key: 'shared_use_annex',
        label: 'Bilaga – nyttjandetid',
        type: 'attachment_ref',
        section: 'samnyttjande',
        requiredAtFinal: true,
        attachmentDefaultName: 'Nyttjandetider för bilplatsen',
        visibleWhen: { shared_use: 'Enligt bilaga' },
        help: 'Välj eller ladda upp bilagan som anger när bilplatsen får nyttjas.',
    },
    {
        key: 'shared_days_1',
        label: 'Veckodagar (period 1)',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Endast nedanstående dagar och tider' },
        help: 'T.ex. "måndag–fredag"',
    },
    {
        key: 'shared_time_1_from',
        label: 'Från kl (period 1)',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Endast nedanstående dagar och tider' },
        help: 'T.ex. "08:00"',
    },
    {
        key: 'shared_time_1_to',
        label: 'Till kl (period 1)',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Endast nedanstående dagar och tider' },
        help: 'T.ex. "17:00"',
    },
    {
        key: 'shared_days_2',
        label: 'Veckodagar (period 2)',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Endast nedanstående dagar och tider' },
        help: 'Valfri – om en andra tidsperiod gäller',
    },
    {
        key: 'shared_time_2_from',
        label: 'Från kl (period 2)',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Endast nedanstående dagar och tider' },
    },
    {
        key: 'shared_time_2_to',
        label: 'Till kl (period 2)',
        type: 'text',
        section: 'samnyttjande',
        visibleWhen: { shared_use: 'Endast nedanstående dagar och tider' },
    },
    // §6 Elström
    {
        key: 'electricity',
        label: 'Elanslutning',
        type: 'select',
        section: 'elstrom',
        requiredAtFinal: true,
        options: [
            { value: 'Utan elanslutning', label: 'Utan elanslutning', code: 'utan_el', summary: 'The space has no electrical connection.' },
            { value: 'Med elanslutning för motorvärmare/kupévärmare', label: 'Med elanslutning för motorvärmare/kupévärmare', code: 'motorvarmare', summary: 'Electrical connection for an engine/cabin heater.' },
            { value: 'Med elanslutning för annat ändamål', label: 'Med elanslutning för annat ändamål', code: 'annat_andamal', summary: 'Electrical connection for another specified purpose.' },
            { value: 'Med laddare för laddbart fordon', label: 'Med laddare för laddbart fordon', code: 'laddare', summary: 'Charger for a rechargeable vehicle.' },
        ],
    },
    {
        key: 'electricity_other_use',
        label: 'Annat ändamål',
        type: 'text',
        section: 'elstrom',
        visibleWhen: { electricity: 'Med elanslutning för annat ändamål' },
        help: 'Beskriv ändamålet',
    },
    {
        key: 'electricity_max_watt',
        label: 'Högsta sammanlagda effekt (watt)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'elstrom',
        visibleWhen: { electricity: 'Med elanslutning för annat ändamål' },
    },
    {
        key: 'charger_type',
        label: 'Anslutningstyp för laddare',
        type: 'text',
        section: 'elstrom',
        visibleWhen: { electricity: 'Med laddare för laddbart fordon' },
        help: 't.ex. motorvärmare',
    },
    // §7 Upplåtelsetid
    {
        key: 'start_date',
        label: 'Från och med',
        type: 'date',
        section: 'upplatelsetid',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'end_date',
        label: 'Till och med',
        type: 'date',
        section: 'upplatelsetid',
        requiredAtFinal: true,
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    // §8 Uppsägningstid / Förlängningstid
    {
        key: 'notice_months',
        label: 'Uppsägningstid (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        requiredAtFinal: true,
        help: 'Skriftlig uppsägning före upplåtelsetidens utgång.',
    },
    {
        key: 'extension_months',
        label: 'Förlängningstid (månader)',
        type: 'number',
        min: 0,
        integer: true,
        section: 'uppsagning',
        requiredAtFinal: true,
        help: 'Avtalet förlängs automatiskt om uppsägning ej sker.',
    },
    // §9 Avgift
    {
        key: 'annual_fee',
        label: 'Avgift (kr/år)',
        type: 'currency',
        min: 0,
        section: 'avgift',
        // Monetary amount — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
        requiredAtFinal: true,
    },
    {
        key: 'fee_inclusion',
        label: 'Avgiftsstruktur',
        type: 'select',
        section: 'avgift',
        requiredAtFinal: true,
        default: 'Total avgift',
        options: [
            { value: 'Total avgift', label: 'Total avgift', code: 'total', summary: 'The fee is all-inclusive (total fee).' },
            { value: 'Avgift exkluderande följande tillägg', label: 'Avgift exkluderande följande tillägg', code: 'exkl_tillagg', summary: 'The fee excludes the add-ons listed below, billed separately.' },
        ],
    },
    {
        key: 'electricity_cost',
        label: '7.1 Kostnad för elström',
        type: 'select',
        section: 'avgift',
        options: [
            { value: 'Ingår i avgiften', label: 'Ingår i avgiften', code: 'ingar', summary: 'Electricity cost is included in the fee.' },
            { value: 'Faktureras efter mätning', label: 'Faktureras efter mätning', code: 'efter_matning', summary: 'Electricity is invoiced based on metering.' },
            { value: 'Utgår enligt mätare för bilplatsen', label: 'Utgår enligt mätare för bilplatsen', code: 'enligt_matare', summary: 'Electricity is charged per the meter for the parking space.' },
            { value: 'Beräknas på bilplatsens del i gemensam strömförbrukning', label: 'Beräknas på bilplatsens del i gemensam strömförbrukning', code: 'gemensam_del', summary: "Electricity is calculated as the space's share of shared consumption." },
            { value: 'Beräknas på följande sätt', label: 'Beräknas på följande sätt', code: 'eget_satt', summary: 'Electricity is calculated by a custom method described below.' },
        ],
    },
    {
        key: 'electricity_cost_basis',
        label: 'Beräkningsgrund (elström)',
        type: 'textarea',
        section: 'avgift',
        visibleWhen: { electricity_cost: 'Beräknas på följande sätt' },
    },
    {
        key: 'cleaning_cost',
        label: '7.2 Städning',
        type: 'select',
        section: 'avgift',
        options: [
            { value: 'Ingår i avgiften', label: 'Ingår i avgiften', code: 'ingar', summary: 'Cleaning cost is included in the fee.' },
            { value: 'Debiteras särskilt', label: 'Debiteras särskilt – med utgångspunkt i bilplatsens del i gemensam städkostnad', code: 'sarskilt', summary: "Cleaning is billed separately based on the space's share of shared cleaning cost." },
        ],
    },
    {
        key: 'snow_cost',
        label: '7.3 Snöröjning',
        type: 'select',
        section: 'avgift',
        options: [
            { value: 'Ingår i avgiften', label: 'Ingår i avgiften', code: 'ingar', summary: 'Snow clearing cost is included in the fee.' },
            { value: 'Debiteras särskilt', label: 'Debiteras särskilt – med utgångspunkt i bilplatsens del i gemensam snöröjningskostnad', code: 'sarskilt', summary: "Snow clearing is billed separately based on the space's share of shared snow-clearing cost." },
        ],
    },
    // §10 Mervärdesskatt
    {
        key: 'vat_status',
        label: 'Mervärdesskatt',
        type: 'select',
        section: 'moms',
        requiredAtFinal: true,
        options: [
            { value: 'Mervärdesskattepliktig vid undertecknande', label: 'Mervärdesskattepliktig vid undertecknande', code: 'momspliktig', summary: 'The letting is subject to VAT at the time of signing.' },
            { value: 'Inte mervärdesskattepliktig vid undertecknande', label: 'Inte mervärdesskattepliktig vid undertecknande', code: 'ej_momspliktig', summary: 'The letting is not subject to VAT at the time of signing.' },
        ],
    },
    // §11 Ändring av avgift
    {
        key: 'fee_adjustment_model',
        label: 'Justeringsmodell',
        type: 'select',
        section: 'andring-avgift',
        requiredAtFinal: true,
        options: [
            { value: 'Indexreglering (KPI 2020)', label: 'Indexreglering (KPI, basår 2020)', code: 'index_kpi', summary: 'Fee is index-adjusted to the CPI (base year 2020).' },
            { value: 'Fast årlig höjning', label: 'Fast årlig höjning', code: 'fast_hojning', summary: 'Fee increases by a fixed percentage each year.' },
        ],
    },
    {
        key: 'base_fee_amount',
        label: 'Basavgift (kr)',
        type: 'currency',
        min: 0,
        section: 'andring-avgift',
        visibleWhen: { fee_adjustment_model: 'Indexreglering (KPI 2020)' },
        // Monetary amount — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'fee_index_alt_base_year',
        label: 'Avvikande bastal (år)',
        type: 'text',
        section: 'andring-avgift',
        visibleWhen: { fee_adjustment_model: 'Indexreglering (KPI 2020)' },
        help: 'Lämna tomt för standardbastal.',
    },
    {
        key: 'fixed_increase_start_date',
        label: 'Första höjning från och med',
        type: 'date',
        section: 'andring-avgift',
        visibleWhen: { fee_adjustment_model: 'Fast årlig höjning' },
        // Absolute date — per-contract, opt-in on copy (ADR-0050).
        scopes: { copy: 'manual' },
    },
    {
        key: 'fixed_increase_percent',
        label: 'Höjning per år (%)',
        type: 'number',
        min: 0,
        max: 100,
        section: 'andring-avgift',
        visibleWhen: { fee_adjustment_model: 'Fast årlig höjning' },
    },
    // §12 Avgiftens betalning
    {
        key: 'payment_period',
        label: 'Betalningsperiod',
        type: 'select',
        section: 'betalning',
        requiredAtFinal: true,
        default: 'Kalenderkvartal',
        options: [
            { value: 'Kalenderkvartal', label: 'Kalenderkvartal', code: 'kvartal', summary: 'Fee is paid per calendar quarter.' },
            { value: 'Kalendermånad', label: 'Kalendermånad', code: 'manad', summary: 'Fee is paid per calendar month.' },
        ],
    },
    {
        key: 'payment_method',
        label: 'Betalningssätt',
        type: 'select',
        section: 'betalning',
        requiredAtFinal: true,
        default: 'Enligt avisering',
        options: [
            { value: 'Enligt avisering', label: 'Enligt avisering (rekommenderas)', code: 'avisering', summary: 'Paid per invoice notice (recommended).' },
            { value: 'Plusgiro', label: 'Plusgiro', code: 'plusgiro', summary: 'Paid to a plusgiro account number.' },
            { value: 'Bankgiro', label: 'Bankgiro', code: 'bankgiro', summary: 'Paid to a bankgiro account number.' },
        ],
    },
    {
        key: 'payment_account',
        label: 'Konto (PG/BG-nummer)',
        type: 'text',
        section: 'betalning',
        visibleWhen: { payment_method: ['Plusgiro', 'Bankgiro'] },
        help: 'T.ex. 123-4567',
    },
    // §14 Bilagor — auto-generated from the contract's bilagor bundle
    // (per ADR-0028 addendum 2026-05-27); no hand-typed list field.
];
export const sampleValues = {
    lessor_name: 'Bostadsägarna Stockholm AB',
    lessor_org_number: '556123-4567',
    lessor_address: 'Drottninggatan 100\n111 60 Stockholm',
    lessor_phone: '08-123 45 67',
    user_name: 'Anna Andersson',
    user_org_number: '19850102-1234',
    user_address: 'Götgatan 35\n116 21 Stockholm',
    user_phone: '070-123 45 67',
    property_municipality: 'Stockholm',
    property_designation: 'Söderhuset 14',
    bilplats_address: 'Götgatan 35\n116 21 Stockholm',
    bilplats_number: '17',
    bilplats_no_specific_spot: false,
    roof_status: 'Har tak',
    area_sqm: 12,
    car_count: 1,
    ev_count: 1,
    shared_use: 'Alla dagar och tider',
    electricity: 'Med laddare för laddbart fordon',
    charger_type: 'Type 2',
    start_date: '2026-09-01',
    end_date: '2027-08-31',
    notice_months: 3,
    extension_months: 12,
    annual_fee: 18000,
    fee_inclusion: 'Avgift exkluderande följande tillägg',
    electricity_cost: 'Faktureras efter mätning',
    cleaning_cost: 'Ingår i avgiften',
    snow_cost: 'Ingår i avgiften',
    vat_status: 'Inte mervärdesskattepliktig vid undertecknande',
    fee_adjustment_model: 'Indexreglering (KPI 2020)',
    base_fee_amount: 18000,
    payment_period: 'Kalenderkvartal',
    payment_method: 'Bankgiro',
    payment_account: '5051-2345',
};
const cond = (mode, predicate) => mode === 'blank' ? true : predicate;
export function Letterhead() {
    return _jsx(TemplateLetterhead, { title: metadata.title, formNumber: metadata.formNumber });
}
export function PageFooter() {
    return _jsx(TemplateFooter, { formNumber: metadata.formNumber });
}
export function Body({ values, mode, attachments, signatories, }) {
    const isIndex = values.fee_adjustment_model === 'Indexreglering (KPI 2020)';
    const isFixed = values.fee_adjustment_model === 'Fast årlig höjning';
    return (_jsxs(SectionNumberingProvider, { sections: metadata.sections ?? [], values: values, mode: mode, children: [_jsx("p", { className: "mb-4 italic text-stone-600", children: metadata.preamble }), _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(Section, { sectionRef: "upplatare", title: "Uppl\u00E5tare", children: _jsx(PartyBlock, { metadata: metadata, slot: "lessor", values: values }) }), _jsx(Section, { sectionRef: "nyttjanderattshavare", title: "Nyttjander\u00E4ttshavare", children: _jsx(PartyBlock, { metadata: metadata, slot: "user", values: values }) })] }), _jsxs(Section, { sectionRef: "bilplats-lage", title: "Bilplatsens l\u00E4ge, nummer och typ", children: [_jsxs("p", { className: "mb-2 text-[10px] leading-relaxed text-stone-700", children: ["Bilplatsen \u00E4r bel\u00E4gen i", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.property_municipality) }), ' ', "kommun, i fastigheten", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.property_designation) }), ". Bilplatsen har adressen", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.bilplats_address) }), "."] }), _jsx(FieldRow, { label: "Bilplatsnummer", children: values.bilplats_no_specific_spot === true
                            ? 'Ingen särskild angiven plats'
                            : v(values.bilplats_number) }), _jsxs(FieldRow, { label: "Tak", children: [_jsxs("span", { className: "mr-2", children: [_jsx("span", { className: "mr-1", children: values.roof_status === 'Har tak' ? '☑' : '☐' }), "har tak"] }), _jsxs("span", { children: [_jsx("span", { className: "mr-1", children: values.roof_status === 'Har inte tak' ? '☑' : '☐' }), "har inte tak"] })] }), _jsx(FieldRow, { label: "Yta (m\u00B2)", children: num(values.area_sqm) })] }), _jsxs(Section, { sectionRef: "anvandning", title: "Anv\u00E4ndning", children: [_jsx("p", { className: "mb-2 text-[10px] leading-relaxed text-stone-700", children: "Bilplatsen uppl\u00E5ts, om inget annat anges, i befintligt skick att anv\u00E4ndas till uppst\u00E4llningsplats f\u00F6r av nyttjander\u00E4ttshavaren nyttjande:" }), _jsxs(FieldRow, { label: "Personbilar", children: [num(values.car_count), " st."] }), _jsxs(FieldRow, { label: "Eldrivna personbilar med laddning", children: [num(values.ev_count), " st."] })] }), _jsxs(Section, { sectionRef: "samnyttjande", title: "Samnyttjande", children: [_jsx(FieldRow, { label: "Nyttjandetid", children: v(values.shared_use) }), cond(mode, values.shared_use === 'Enligt bilaga') && (_jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["Bilplatsen f\u00E5r nyttjas enligt vad som anges i", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: attachmentRefLabel(attachments, values.shared_use_annex) }), "."] })), cond(mode, values.shared_use === 'Endast nedanstående dagar och tider') && (_jsxs(_Fragment, { children: [_jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["Veckodagarna", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_days_1) }), ' ', "mellan kl", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_time_1_from) }), ' ', "och kl", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_time_1_to) }), "."] }), cond(mode, !!values.shared_days_2) && (_jsxs("p", { className: "mt-1 text-[10px] leading-relaxed text-stone-700", children: ["Samt veckodagarna", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_days_2) }), ' ', "mellan kl", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_time_2_from) }), ' ', "och kl", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.shared_time_2_to) }), "."] })), _jsx("p", { className: "mt-1 text-[10px] italic text-stone-600", children: "\u00D6vriga tider disponeras bilplatsen med tillh\u00F6rande utrustning av uppl\u00E5taren eller annan nyttjander\u00E4ttshavare." })] }))] }), _jsxs(Section, { sectionRef: "elstrom", title: "Elstr\u00F6m", children: [_jsx("p", { className: "mb-2 text-[10px] italic text-stone-600", children: "Bilplatsen uppl\u00E5ts:" }), _jsx(FieldRow, { label: "Anslutning", children: v(values.electricity) }), cond(mode, values.electricity === 'Med elanslutning för annat ändamål') && (_jsxs(_Fragment, { children: [_jsx(FieldRow, { label: "Annat \u00E4ndam\u00E5l", children: v(values.electricity_other_use) }), _jsx(FieldRow, { label: "H\u00F6gsta sammanlagda effekt (watt)", children: num(values.electricity_max_watt) })] })), cond(mode, values.electricity === 'Med laddare för laddbart fordon') && (_jsx(FieldRow, { label: "Anslutningstyp", children: v(values.charger_type) }))] }), _jsx(Section, { sectionRef: "upplatelsetid", title: "Uppl\u00E5telsetid", children: _jsxs("div", { className: "grid grid-cols-2 gap-6", children: [_jsx(FieldRow, { label: "Fr\u00E5n och med", children: v(values.start_date) }), _jsx(FieldRow, { label: "Till och med", children: v(values.end_date) })] }) }), _jsx(Section, { sectionRef: "uppsagning", title: "Upps\u00E4gningstid / F\u00F6rl\u00E4ngningstid", children: _jsxs("p", { className: "text-[10px] leading-relaxed text-stone-700", children: ["Upps\u00E4gning av detta kontrakt ska ske skriftligen minst", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.notice_months) }), ' ', "m\u00E5nad(er) f\u00F6re den avtalade uppl\u00E5telsetidens utg\u00E5ng. I annat fall \u00E4r kontraktet f\u00F6r varje g\u00E5ng f\u00F6rl\u00E4ngt med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.extension_months) }), ' ', "m\u00E5nad(er)."] }) }), _jsxs(Section, { sectionRef: "avgift", title: "Avgift", children: [_jsxs("p", { className: "mb-2 text-[10px] leading-relaxed text-stone-700", children: ["Kronor", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.annual_fee) }), ' ', "per \u00E5r utg\u00F6rande", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.fee_inclusion) }), "."] }), _jsx("div", { className: "mt-2 mb-1 font-sans text-[10px] uppercase tracking-[0.1em] text-stone-500", children: "7.1 Kostnad f\u00F6r elstr\u00F6m" }), _jsx(FieldRow, { label: "Hantering", children: v(values.electricity_cost) }), cond(mode, values.electricity_cost === 'Beräknas på följande sätt') && (_jsx(FieldRow, { label: "Ber\u00E4kningsgrund", children: v(values.electricity_cost_basis) })), _jsx("div", { className: "mt-3 mb-1 font-sans text-[10px] uppercase tracking-[0.1em] text-stone-500", children: "7.2 St\u00E4dning" }), _jsx(FieldRow, { label: "Hantering", children: v(values.cleaning_cost) }), _jsx("div", { className: "mt-3 mb-1 font-sans text-[10px] uppercase tracking-[0.1em] text-stone-500", children: "7.3 Sn\u00F6r\u00F6jning" }), _jsx(FieldRow, { label: "Hantering", children: v(values.snow_cost) })] }), _jsxs(Section, { sectionRef: "moms", title: "Merv\u00E4rdesskatt (moms)", children: [cond(mode, values.vat_status === 'Mervärdesskattepliktig vid undertecknande') && (_jsx("p", { className: "text-[10px] leading-relaxed text-stone-700", children: "Vid avtalets undertecknande \u00E4r uppl\u00E5telsen merv\u00E4rdesskattepliktig enligt merv\u00E4rdesskattelagen (2023:200). Nyttjander\u00E4ttshavaren ska ut\u00F6ver avgiften betala vid varje tillf\u00E4lle g\u00E4llande moms." })), cond(mode, values.vat_status === 'Inte mervärdesskattepliktig vid undertecknande') && (_jsx("p", { className: "text-[10px] leading-relaxed text-stone-700", children: "Vid avtalets undertecknande \u00E4r uppl\u00E5telsen inte merv\u00E4rdesskattepliktig. Om uppl\u00E5telsen blir merv\u00E4rdesskattepliktig ska nyttjander\u00E4ttshavaren ut\u00F6ver avgiften betala vid varje tillf\u00E4lle g\u00E4llande moms." })), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Momsen, som ska betalas samtidigt med avgiften, ber\u00E4knas p\u00E5 avgiften inklusive eventuella till\u00E4gg eller andra ers\u00E4ttningar enligt vid varje tillf\u00E4lle g\u00E4llande skattesats." })] }), _jsxs(Section, { sectionRef: "andring-avgift", title: "\u00C4ndring av avgift", children: [_jsx(FieldRow, { label: "Modell", children: v(values.fee_adjustment_model) }), cond(mode, isIndex) && (_jsxs(_Fragment, { children: [cond(mode, !!values.fee_index_alt_base_year) && (_jsx(FieldRow, { label: "Avvikande bastal", children: v(values.fee_index_alt_base_year) })), _jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["Avgiften \u00E4ndras under l\u00F6pande avtal i enlighet med f\u00F6ljande indexklausul. Under nyttjander\u00E4ttsf\u00F6rh\u00E5llandet ska avgiften uppg\u00E5 till", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.base_fee_amount) }), ' ', "kr och utg\u00F6ra basavgift."] }), _jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["Under nyttjander\u00E4ttstiden ska till\u00E4gg till basavgiften utg\u00E5 med h\u00E4nsyn till f\u00F6r\u00E4ndringar i konsumentprisindex (totalindex med 2020 som bas\u00E5r). F\u00F6r avtal som b\u00F6rjar l\u00F6pa under perioden 1 januari\u201330 juni anses basavgiften anpassad till indextalet f\u00F6r oktober m\u00E5nad f\u00F6reg\u00E5ende \u00E5r. F\u00F6r avtal som b\u00F6rjar l\u00F6pa under perioden 1 juli\u201331 december anses basavgiften i st\u00E4llet anpassad till indextalet f\u00F6r oktober m\u00E5nad samma \u00E5r. Det indextalet utg\u00F6r bastalet, om inte parterna skriftligen avtalat om avvikande bastal.", values.fee_index_alt_base_year ? (_jsxs(_Fragment, { children: [' ', "Har avvikande bastal avtalats, utg\u00F6rs bastalet av indextalet f\u00F6r oktober m\u00E5nad \u00E5r", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.fee_index_alt_base_year) }), "."] })) : null] }), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Skulle indextalet f\u00F6r en p\u00E5f\u00F6ljande oktoberm\u00E5nad ha stigit eller sjunkit i f\u00F6rh\u00E5llande till bastalet, ska till\u00E4gget motsvara den procentuella f\u00F6r\u00E4ndringen mellan bastalet och det aktuella indextalet f\u00F6r respektive oktoberm\u00E5nad. Till\u00E4gget kan s\u00E5v\u00E4l \u00F6ka som minska mellan olika \u00E5r. Avgiften ska dock aldrig understiga det i avtalet angivna basavgiftsbeloppet. Avgifts\u00E4ndringen tr\u00E4der i kraft fr\u00E5n och med den 1 januari n\u00E4rmast efter det att oktobertalet f\u00F6ranlett omr\u00E4kning. Det justerade till\u00E4gget avrundas till n\u00E4rmaste hela krontal." }), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Om bastalet utg\u00F6rs av ett indextal fastst\u00E4llt med bas\u00E5r 1980 ska det omr\u00E4knas till index med bas\u00E5r 2020 enligt f\u00F6ljande formel: bastalet divideras med det officiellt fastst\u00E4llda medeltalet av KPI f\u00F6r \u00E5r 2020 uttryckt med bas\u00E5r 1980 \u2013 f\u00F6r n\u00E4rvarande 335,92 \u2013 och kvoten multipliceras med 100. Resultatet avrundas till tv\u00E5 decimaler. Har SCB fastst\u00E4llt ett reviderat medeltal f\u00F6r 2020 vid tidpunkten f\u00F6r omr\u00E4kning, ska det reviderade talet anv\u00E4ndas." })] })), cond(mode, isFixed) && (_jsxs("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: ["Den 1 januari varje \u00E5r, fr\u00E5n och med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.fixed_increase_start_date) }), ", ska den vid tidpunkten g\u00E4llande avgiften h\u00F6jas med", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: num(values.fixed_increase_percent) }), ' ', "procent."] }))] }), _jsxs(Section, { sectionRef: "betalning", title: "Avgiftens betalning", children: [_jsxs("p", { className: "text-[10px] leading-relaxed text-stone-700", children: ["Avgiften tillsammans med f\u00F6rekommande till\u00E4gg betalas i f\u00F6rskott senast sista vardagen f\u00F6re b\u00F6rjan p\u00E5 varje", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: values.payment_period === 'Kalendermånad' ? 'kalendermånad' : 'kalenderkvartal' }), "."] }), _jsx(FieldRow, { label: "Betalningss\u00E4tt", children: v(values.payment_method) }), cond(mode, values.payment_method === 'Plusgiro' || values.payment_method === 'Bankgiro') && (_jsx(FieldRow, { label: values.payment_method === 'Plusgiro' ? 'Plusgiro nr' : 'Bankgiro nr', children: v(values.payment_account) })), _jsx("p", { className: "mt-2 text-[10px] leading-relaxed text-stone-700", children: "Vid f\u00F6rsenad betalning ska nyttjander\u00E4ttshavaren betala dr\u00F6jsm\u00E5lsr\u00E4nta och ers\u00E4ttning f\u00F6r betalningsp\u00E5minnelse, inkasso och dylikt enligt lag." })] }), _jsxs(Section, { sectionRef: "avtalsvillkor", title: "Avtalsvillkor", children: [_jsx("p", { className: "m-0 whitespace-pre-wrap text-[10.5px] leading-relaxed", children: 'Nyttjanderättshavaren får inte utöver befintliga skyddsanordningar sätta upp ytterligare sådana eller andra anordningar av permanent eller tillfällig karaktär.\n\n' +
                            'Önskar nyttjanderättshavaren sätta upp skylt med bilnummer ska skylten och uppsättningsanordningen godkännas av upplåtaren.\n\n' +
                            'Nyttjanderättshavaren förbinder sig att på bilplatsen inte utan upplåtarens samtycke låta uppställa fordonsvrak.\n\n' +
                            'Underhåll av bilplatsen och därtill hörande anordningar med undantag av ovan nämnda skylt bekostas och ombesörjs av upplåtaren.\n\n' +
                            'Nyttjanderättshavaren får inte låta utföra biltvätt på bilplatsen.\n\n' +
                            'Upplåtaren har rätt till att få tillträde till bilplatsen för att utföra nödvändiga reparations- och underhållsarbeten.\n\n' +
                            'För infart till och utfart från bilplatsen ska nyttjanderättshavaren använda mark som har iordningställts för ändamålet.\n\n' +
                            'Om inte annat har angetts under särskilda bestämmelser svarar upplåtaren för sedvanlig renhållning, snöröjning och sandning på infartsväg till bilplatsen medan nyttjanderättshavaren svarar för sådana åtgärder när det gäller själva bilplatsen.\n\n' +
                            'Upplåtaren friskriver sig från ansvar för all skada på nyttjanderättshavarens fordon och på egendom som förvaras i fordonet, såvida skadan inte har uppkommit genom upplåtarens eget vållande. Upplåtaren friskriver sig också från ansvar för intrång från obehöriga.\n\n' +
                            'För tillfälligt hinder eller men i nyttjanderätten till följd av underhållsarbeten på bilplatsen eller andra därmed jämförbara förhållanden har nyttjanderättshavaren inte rätt till nedsättning av avgiften.' }), cond(mode, values.electricity === 'Med elanslutning för motorvärmare/kupévärmare' ||
                        values.electricity === 'Med elanslutning för annat ändamål' ||
                        values.electricity === 'Med laddare för laddbart fordon') && (_jsx(ClauseBlock, { title: "S\u00E4rskilt f\u00F6r bilplats med eluttag eller laddare", body: 'För upplåtelse av bilplats med eluttag eller laddare gäller särskilt:\n\n' +
                            '• att för ändamålet godkänd adapter och anslutningskabel utöver fast kabel tillhandahålls av nyttjanderättshavaren,\n' +
                            '• att endast elapparat, adapter och anslutningskabel som uppfyller gällande säkerhetskrav får användas,\n' +
                            '• att eluttaget inte får användas för laddning av el-/hybridfordon om det inte särskilt godkänts av upplåtaren,\n' +
                            '• att eluttaget alltid ska hållas låst,\n' +
                            '• att anslutningskabel inte får vara ansluten till eluttaget då kabeln inte är kopplad till elapparat,\n' +
                            '• att nyttjanderättshavaren omedelbart till upplåtaren ska anmäla uppkomna fel på eluttaget eller laddaren,\n' +
                            '• att nyttjanderättshavaren ansvarar för att elapparat, anslutet fordon, anslutningskabel och adapter vid användningen är i fullgott skick,\n' +
                            '• att endast det eluttag eller laddpunkt som tillhör bilplatsen får användas,\n' +
                            '• att säkerhetsföreskrifter och bruksanvisning som framgår av anslag vid laddaren eller som tillställts nyttjanderättshavaren på annat sätt iakttas vid användning av laddaren.' })), _jsx(ClauseBlock, { title: "\u00D6verl\u00E5telse, f\u00F6rverkande och ordningsf\u00F6reskrifter", body: 'Nyttjanderätten till bilplatsen enligt detta avtal får inte utan upplåtarens medgivande överlåtas eller inskrivas.\n\n' +
                            'Nyttjanderättshavarens rätt enligt detta avtal är förverkad och upplåtaren berättigad att säga upp avtalet till omedelbart upphörande om nyttjanderättshavaren inte i rätt tid betalar angiven avgift tillsammans med förekommande tillägg och moms eller i övrigt bryter mot bestämmelserna i 8 kap. 23 § jordabalken.\n\n' +
                            'För nyttjanderätten av bilplatsen gäller särskilda ordningsföreskrifter.' })] }), _jsx(Section, { sectionRef: "bilagor", title: "Bilagor", children: _jsx(AttachmentList, { attachments: attachments }) }), _jsx(Section, { sectionRef: "underskrift", title: "Underskrifter", children: _jsx(KeepTogether, { children: _jsx(SigningBlock, { statement: true, parties: [
                            { label: 'Upplåtare', role: 'lessor', ordinal: 0 },
                            { label: 'Nyttjanderättshavare', role: 'lessee', ordinal: 0 },
                        ], signatories: signatories }) }) })] }));
}
export function Component(props) {
    return (_jsx(TemplateDocument, { header: _jsx(Letterhead, {}), footer: _jsx(PageFooter, {}), children: _jsx(Body, { ...props }) }));
}
//# sourceMappingURL=index.js.map